import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Calendar, Play, Plus, Check } from "lucide-react";
import { Reveal, SectionLabel, MaskedLine } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Layout";
import { DIAG_URL } from "@/links";

const PAINS = [
  { n: "01", t: "Desconto virou argumento de venda", d: "Sem diferenciais comprovados, todo fechamento vira negociação de preço — e a margem paga a conta." },
  { n: "02", t: "Cada canal conta uma história", d: "Site, apresentação, WhatsApp e redes sociais dizem coisas diferentes. O mercado percebe a incoerência." },
  { n: "03", t: "Decisões no feeling", d: "Sem diagnóstico, cada campanha é uma aposta. O que não performa vira culpa do canal, nunca da direção." },
  { n: "04", t: "Crescimento dependente de gente", d: "O faturamento só anda quando o fundador empurra. Demanda qualificada não chega sozinha." },
];

const STEPS = [
  {
    id: "D1",
    t: "Diagnóstico",
    benefit: "Um espelho imparcial do negócio",
    items: ["Relatório Consolidado de Diagnóstico Estratégico", "Mapa de posicionamento e matriz concorrencial", "Síntese: preservar, corrigir, abandonar, acelerar"],
  },
  {
    id: "D2",
    t: "Direção",
    benefit: "A razão de escolher você — e mais ninguém",
    items: ["Plataforma de Marca e posicionamento competitivo", "Manifesto Institucional de Posicionamento", "Matriz de Voz e Arquitetura de Ofertas"],
  },
  {
    id: "D3",
    t: "Desenho",
    benefit: "Uma marca reconhecida de relance",
    items: ["Manual de Identidade Visual e Verbal", "Apresentação Comercial Institucional Master", "Modelos para redes, propostas e papelaria digital"],
  },
  {
    id: "D4",
    t: "Desdobramento",
    benefit: "Estratégia virando rotina e retorno",
    items: ["Plano de Ativação e Distribuição de Canais", "Jornada Comercial e Funil de Vendas", "Painel Executivo e roadmap de 90 dias a 12 meses"],
  },
];

const OUTCOMES = [
  ["Clareza executiva", "Uma proposição inequívoca que o time comercial articula com naturalidade."],
  ["Consistência de discurso", "O mesmo tom no site, nas apresentações, no WhatsApp e na sala de reunião."],
  ["Percepção de valor", "Lastro técnico para defender preço sem perder para concorrentes populares."],
  ["Demanda qualificada", "Canais filtrados pela mensagem certa, com objeções desmontadas antes da proposta."],
  ["Recomendação espontânea", "Clientes que sabem para quem — e como — indicar o seu serviço."],
  ["Governança estratégica", "Rituais trimestrais guiados por indicadores de percepção e pipeline."],
];

const FAQ = [
  {
    q: "Quanto tempo leva a Plataforma Completa?",
    a: "O D1 Diagnóstico leva de 10 a 15 dias. As etapas seguintes são sequenciais — cada uma começa sobre a base validada da anterior — com cronograma operacional definido na formalização. O roadmap de implementação se estende por 90 dias, 180 dias e 12 meses.",
  },
  {
    q: "Preciso refazer logo ou site antes de começar?",
    a: "Não. Cognição antes da estética: nenhuma linha de design é traçada antes de a estratégia estar decidida. A identidade só é desenhada no D3, com posicionamento comprovado no D1 e no D2.",
  },
  {
    q: "A inteligência artificial substitui o trabalho estratégico?",
    a: "Não. Os agentes de IA da Fuchs cuidam da tabulação e das correlações do briefing em tempo reduzido. O julgamento crítico e as decisões de direção são de gente — sênior, ao lado da sua liderança.",
  },
  {
    q: "Já tenho agência ou time de marketing. A 4D substitui?",
    a: "Não substitui — dá direção. Com posicionamento e arquitetura de marca definidos, sua agência e seu time executam com lastro, sem desperdiçar verba em tentativa e erro.",
  },
  {
    q: "Qual é o investimento?",
    a: "A proposta é calibrada depois do diagnóstico, conforme o tamanho do desafio. E há uma garantia de método: se você avançar para a Plataforma Completa em até 30 dias após o relatório do D1, 100% do valor do diagnóstico vira crédito.",
  },
];

const FaqItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={`faq-item-${i}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-question-${i}`}
        className="group flex w-full items-center justify-between gap-6 py-7 text-left"
      >
        <span className="font-display text-lg font-bold text-white transition-colors group-hover:text-brand-orange sm:text-xl">
          {q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className={`flex h-9 w-9 shrink-0 items-center justify-center border transition-colors ${
            open ? "border-brand-orange text-brand-orange" : "border-white/20 text-slate-400"
          }`}
        >
          <Plus size={16} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="max-w-3xl pb-7 text-sm leading-relaxed text-slate-400 sm:text-base">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Quando o vídeo estiver gravado, cole a URL aqui (link do YouTube ou arquivo .mp4)
// e o player entra no lugar do placeholder automaticamente.
const VIDEO_URL = "https://youtube.com/shorts/sEPhpMaSanc";

const youtubeId = (url) => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

const IS_SHORT = VIDEO_URL.includes("/shorts/");

const VideoSection = () => {
  const yt = VIDEO_URL ? youtubeId(VIDEO_URL) : null;
  return (
    <section data-testid="sales-video-section" className="relative bg-ink py-20 lg:py-28">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <Reveal className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <SectionLabel>Direto de quem conduz o método</SectionLabel>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Antes de oferecer a você,{" "}
              <span className="font-serif font-medium italic text-brand-orange">nós validamos!</span>
            </h2>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <div className={`relative ${IS_SHORT ? "mx-auto max-w-[380px]" : ""}`}>
            <div aria-hidden className="absolute -left-3 -top-3 h-full w-full border border-brand-orange/40" />
            <div className={`relative overflow-hidden border border-white/10 bg-navy ${IS_SHORT ? "aspect-[9/16]" : "aspect-video"}`}>
              {VIDEO_URL ? (
                yt ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${yt}`}
                    title="Danielle apresenta a Plataforma 4D"
                    data-testid="sales-video-frame"
                    className="h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={VIDEO_URL} controls data-testid="sales-video-frame" className="h-full w-full object-cover" />
                )
              ) : (
                <div data-testid="sales-video-placeholder" className="relative h-full w-full">
                  <img
                    src="/assets/danielle-portrait.jpg"
                    alt="Danielle Fuchs"
                    className="h-full w-full object-contain object-center opacity-30"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
                    <motion.span
                      className="flex h-20 w-20 items-center justify-center rounded-full border border-brand-orange/70 bg-ink/60 text-brand-orange backdrop-blur-sm"
                      animate={{ scale: [1, 1.08, 1] }}
                      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Play size={26} className="ml-1" />
                    </motion.span>
                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">
                      Vídeo em produção
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const CTAButtons = ({ testidPrefix }) => (
  <div className="flex flex-col gap-4 sm:flex-row">
    <Link
      to={DIAG_URL}
      data-testid={`${testidPrefix}-diagnostico`}
      className="group inline-flex items-center justify-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red"
    >
      Fazer diagnóstico gratuito
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
    <Link
      to="/agendar"
      data-testid={`${testidPrefix}-agendar`}
      className="group inline-flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
    >
      <Calendar size={16} />
      Falar com um especialista
    </Link>
  </div>
);

const ScheduleCTAButtons = ({ testidPrefix }) => (
  <div className="flex flex-col gap-4 sm:flex-row">
    <Link
      to="/agendar"
      data-testid={`${testidPrefix}-agendar`}
      className="group inline-flex items-center justify-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red"
    >
      <Calendar size={16} />
      Agendar Reunião Estratégica
    </Link>
    <Link
      to={DIAG_URL}
      data-testid={`${testidPrefix}-diagnostico`}
      className="group inline-flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
    >
      Começar pelo diagnóstico
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  </div>
);

export default function Plataforma() {
  return (
    <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <section data-testid="sales-hero" className="hero-glow noise relative overflow-hidden bg-ink pb-24 pt-36 lg:pb-32 lg:pt-48">
        <div aria-hidden className="pointer-events-none absolute -right-10 top-24 select-none font-display text-[30vw] font-extrabold leading-none text-outline opacity-50 lg:text-[20vw]">
          4D
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <SectionLabel>Plataforma 4D · Método completo</SectionLabel>
          </motion.div>
          <h1 className="mt-8 max-w-5xl font-display text-4xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>Posicione sua marca com clareza.</MaskedLine>
            <MaskedLine delay={0.4}>
              Tome decisões com <span className="font-serif font-medium italic text-brand-orange">direção</span>.
            </MaskedLine>
          </h1>
          <motion.p
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            Uma jornada completa de posicionamento e direção que começa entendendo onde sua marca está hoje.
            O Método 4D combina Diagnóstico, Direção, Desenho e Desdobramento, apoiados por plataforma digital
            exclusiva, IA e acompanhamento sênior. Comece gratuitamente pelo diagnóstico. A partir dessa primeira
            leitura, você entende os principais pontos de atenção da sua marca e pode decidir se é o momento de
            avançar para a jornada completa.
          </motion.p>
          <motion.div
            className="mt-10"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
          >
            <CTAButtons testidPrefix="sales-hero-cta" />
          </motion.div>
          <motion.p
            className="mt-10 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
          >
            20+ anos de estratégia · 100+ empresas orientadas · Método validado na própria Fuchs
          </motion.p>
        </div>
      </section>

      <VideoSection />

      <EditorialMarquee />

      <section data-testid="sales-pains" className="bg-ink py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel>O custo invisível</SectionLabel>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Operar sem posicionamento{" "}
              <span className="font-serif font-medium italic text-brand-magenta">cobra todo mês.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2">
            {PAINS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div data-testid={`sales-pain-${p.n}`} className="group h-full bg-ink p-8 transition-colors duration-500 hover:bg-navy lg:p-10">
                  <span className="font-mono text-xs text-brand-orange">{p.n}</span>
                  <h3 className="mt-4 font-display text-xl font-bold text-white transition-colors group-hover:text-brand-orange sm:text-2xl">
                    {p.t}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="sales-ecosystem" className="bg-paper py-24 text-navy lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel light>O que você contrata</SectionLabel>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Não é consultoria de prateleira. É um ecossistema.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {[
              ["Método", "Quatro etapas em ordem lógica, cada uma com entregáveis definidos e critérios objetivos de validação técnica."],
              ["Plataforma", "Área do cliente com painel operacional, marcos do cronograma, indicadores de evolução e radar estratégico."],
              ["Inteligência", "Agentes de IA tabulam e correlacionam os insumos em tempo reduzido. O julgamento crítico permanece humano e sênior."],
            ].map(([k, d], i) => (
              <Reveal key={k} delay={i * 0.1}>
                <div data-testid={`sales-pillar-${k.toLowerCase()}`} className="group h-full border border-navy/15 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-orange lg:p-10">
                  <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-orange">{k}</span>
                  <p className="mt-5 text-sm leading-relaxed text-navy/70">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="sales-deliverables" className="bg-ink py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel>O que você recebe</SectionLabel>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Quatro etapas. <span className="font-serif font-medium italic text-brand-orange">Quatro entregas concretas.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-2">
            {STEPS.map((s, i) => (
              <Reveal key={s.id} delay={(i % 2) * 0.1}>
                <div data-testid={`sales-step-${s.id.toLowerCase()}`} className="group h-full border border-white/10 p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-orange/60 lg:p-10">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-4xl font-extrabold text-outline-orange">{s.id}</span>
                    <div>
                      <h3 className="font-display text-2xl font-bold text-white">{s.t}</h3>
                      <p className="mt-1 font-serif italic text-slate-400">{s.benefit}</p>
                    </div>
                  </div>
                  <ul className="mt-7 space-y-3 border-t border-white/10 pt-7">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                        <Check size={15} className="mt-0.5 shrink-0 text-brand-orange" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <Link
              to="/metodo-4d"
              data-testid="sales-method-link"
              className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
            >
              Ver o detalhamento completo do método
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="sales-outcomes" className="bg-paper py-24 text-navy lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel light>Na prática</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              O que muda no seu negócio.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-3">
            {OUTCOMES.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 3) * 0.08}>
                <div data-testid={`sales-outcome-${i + 1}`} className="group h-full bg-paper p-8 transition-colors duration-500 hover:bg-white">
                  <span className="font-mono text-xs text-brand-orange">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-lg font-bold transition-colors group-hover:text-brand-orange sm:text-xl">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/70">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="sales-proof" className="noise relative bg-navy py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-[480px]">
              <div aria-hidden className="absolute -left-4 -top-4 h-full w-full border border-brand-orange/50" />
              <img
                src="/assets/danielle-portrait.jpg"
                alt="Danielle Fuchs sentada em poltrona vermelha, de blazer azul-marinho"
                data-testid="sales-portrait"
                className="relative aspect-[2/3] w-full object-cover object-center"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel>Por que a Fuchs</SectionLabel>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                O primeiro case do método{" "}
                <span className="font-serif font-medium italic text-brand-orange">é a própria Fuchs.</span>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">
                Antes de levar a Plataforma 4D aos clientes, aplicamos o método na nossa evolução.
                Quinze anos de trajetória, três capítulos e um reposicionamento completo — conduzido
                por Danielle Fuchs, com 20+ anos de decisões estratégicas e 100+ empresas orientadas.
              </p>
            </Reveal>
            <div className="mt-10 space-y-6">
              {[
                "Diagnóstico e direção antes de qualquer estética.",
                "IA para tabular e correlacionar; gente para decidir.",
                "Sem atalhos de crescimento artificial: método, arquitetura de marca e implementação controlada.",
              ].map((t, i) => (
                <Reveal key={t} delay={i * 0.08}>
                  <div className="flex items-start gap-4 border-l-2 border-brand-orange pl-5">
                    <p className="text-sm leading-relaxed text-slate-300 sm:text-base">{t}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="sales-faq" className="bg-ink py-24 lg:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel>Perguntas frequentes</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              O que costumam nos perguntar.
            </h2>
          </Reveal>
          <Reveal className="mt-12 border-t border-white/10">
            <div>
              {FAQ.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} i={i} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="sales-final-cta" className="noise relative overflow-hidden bg-ink py-28 lg:py-40">
        <div aria-hidden className="pointer-events-none absolute inset-0 hero-glow" />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-orange/60 to-transparent" />
        <div className="relative mx-auto max-w-5xl px-6 text-center lg:px-10">
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sua marca já cresceu.{" "}
              <span className="font-serif font-medium italic text-brand-orange">Agora dê direção a ela.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl font-serif text-lg italic leading-relaxed text-slate-400">
              Comece pelo Diagnóstico de Maturidade 4D ou vá direto à conversa estratégica.
            </p>
            <div className="mt-12 flex justify-center">
              <ScheduleCTAButtons testidPrefix="sales-final-cta" />
            </div>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}
